import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import DataTable from "../../../components/RestaurantAdminComponents/DataTable";
import FilterToolbar from "../../../components/Common/FilterToolbar";
import SecondaryButton from "../../../components/Common/SecondaryButton";
import { Container } from "reactstrap";
import ActionMenu from "../../../components/Common/ActionMenu";
import { get, del } from "../../../api/axios";
import endpoints from "../../../api/endpoints";
import toastr from "toastr";
import "toastr/build/toastr.min.css";
import tickIcon from "../../../assets/images/support/tick-circle-white.svg";

/* -------------------------------------------------------------------------
 * RestaurantStaffPage — full "Staff" table view. No sidebar or
 * header here; this is content-only, dropped into whatever shell layout
 * the app already provides.
 *
 * transformStaffData now also carries split firstName/lastName (not just
 * the combined `name`) so any optimistic-UI use of the row data passed
 * via navigation state has the right shape. The edit/create page itself
 * no longer depends on this state for populating the form — it always
 * fetches a fresh record by the :staffId route param — this is just a
 * defensive improvement so the row data is complete either way.
 * ---------------------------------------------------------------------- */

const RestaurantStaffPage = ({ onFilterClick, onExportClick }) => {
  document.title = "Staff | Omaha Food Lovers";
  const navigate = useNavigate();
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("role");
  const [sortOrder, setSortOrder] = useState("desc");
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [staffToDelete, setStaffToDelete] = useState(null);

  toastr.options = {
    positionClass: "toast-bottom-left",
    timeOut: 5000,
    closeButton: true,
    progressBar: true,
  };

  const columns = [
    { key: "name", label: "Name", width: "1.5fr" },
    { key: "email", label: "Email", width: "1.5fr" },
    { key: "designation", label: "Designation", width: "1fr" },
    { key: "isActive", label: "Status", width: "0.8fr" },
    { key: "dateRegister", label: "Date Register", width: "1fr" },
    { key: "createdAt", label: "Created At", width: "1fr" },
    {
      key: "actions",
      label: "",
      width: "60px",
      align: "right",
      className: "rp-table__cell--actions",
      render: (row) => (
        <ActionMenu
          isOpen={activeDropdown === row.id}
          onToggle={() => setActiveDropdown(activeDropdown === row.id ? null : row.id)}
          items={[
            {
              label: "Edit",
              icon: "edit",
              onClick: () => navigate(`/restaurant/staff/${row.userId}/edit`, { state: { staffData: row } }),
            },
            {
              label: "Delete",
              icon: "delete",
              onClick: () => handleDelete(row.userId),
              danger: true,
            },
          ]}
        />
      ),
    },
  ];

  const buildQueryParams = (pageNum = 1) => {
    const params = new URLSearchParams();
    params.append("page", pageNum);
    params.append("per_page", 20);
    params.append("sort_by", sortBy);
    params.append("sort_order", sortOrder);

    if (search.trim()) {
      params.append("search", search.trim());
    }

    return params.toString();
  };

  const transformStaffData = (staffArray) =>
    staffArray.map((staffItem) => {
      let designation = staffItem.designation_type || "N/A";
      // Capitalize first letter and replace "manager" with "Staff"
      if (designation && designation !== "N/A") {
        designation = designation.charAt(0).toUpperCase() + designation.slice(1).toLowerCase();
        designation = designation.replace(/manager/gi, "Staff");
      }
      return {
        id: staffItem.id,
        userId: staffItem.user_id,
        firstName: staffItem.user?.first_name || "",
        lastName: staffItem.user?.last_name || "",
        name:
          `${staffItem.user?.first_name || ""} ${staffItem.user?.last_name || ""}`.trim() ||
          staffItem.user?.full_name ||
          "Unknown",
        email: staffItem.user?.email || "N/A",
        designation: designation,
        isActive: staffItem.user?.is_active === 1 ? "Active" : "Inactive",
        dateRegister: staffItem.user?.date_register || "N/A",
        createdAt: staffItem.created_at ? new Date(staffItem.created_at).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    }) : "N/A",
      };
    });

  const fetchStaff = async (pageNum = 1, isLoadMore = false) => {
    if (isLoadMore) {
      setLoadingMore(true);
    } else {
      setLoading(true);
    }

    try {
      const response = await get(`${endpoints.restaurantDashboardStaff}?${buildQueryParams(pageNum)}`);

      if (response.data?.success) {
        const data = response.data.data;
        const newStaff = data.staff?.data || [];
        const transformedStaff = transformStaffData(newStaff);

        if (isLoadMore) {
          setStaff((prev) => [...prev, ...transformedStaff]);
        } else {
          setStaff(transformedStaff);
        }

        const pagination = data.staff || {};
        const currentPage = pagination.current_page || 1;
        const lastPage = pagination.last_page || 1;
        setHasMore(currentPage < lastPage);
        setPage(pageNum);
      }
    } catch (error) {
      console.error("Failed to fetch staff:", error);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const handleLoadMore = () => {
    if (!loadingMore && hasMore) {
      fetchStaff(page + 1, true);
    }
  };

  const handleSearchChange = (value) => {
    setSearch(value);
    setPage(1);
    setHasMore(true);
  };

  const handleDelete = (staffId) => {
    setStaffToDelete(staffId);
    setShowDeleteModal(true);
    setActiveDropdown(null);
  };

  const confirmDelete = async () => {
    if (!staffToDelete) return;

    try {
      await del(`${endpoints.restaurantDashboardStaff}/${staffToDelete}`);
      toastr.success("Staff member deleted successfully");
      setShowDeleteModal(false);
      setStaffToDelete(null);
      fetchStaff(1, false);
    } catch (error) {
      console.error("Failed to delete staff:", error);
      toastr.error("Failed to delete staff member");
    }
  };

  const cancelDelete = () => {
    setShowDeleteModal(false);
    setStaffToDelete(null);
  };

  const handleExport = async () => {
    try {
      const response = await get(`${endpoints.restaurantExport}?type=staff`, {
        responseType: 'blob',
      });
      
      // Create a blob from the response
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      
      // Create a temporary link to download the file
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'staff_export.csv');
      document.body.appendChild(link);
      link.click();
      
      // Clean up
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toastr.success('Staff data exported successfully');
    } catch (error) {
      console.error('Failed to export staff:', error);
      toastr.error('Failed to export staff data');
    }
  };

  // Filter configuration for FilterToolbar
  const filters = [
    {
      key: "sortBy",
      type: "sortBy",
      label: "Sort By",
      value: sortBy,
      onChange: setSortBy,
      defaultValue: "role",
      options: [
        { value: "role", label: "Role" },
        { value: "is_active", label: "Status" },
      ],
    },
    {
      key: "sortOrder",
      type: "sortDirection",
      label: "Sort Order",
      value: sortOrder,
      onChange: setSortOrder,
      defaultValue: "desc",
    },
  ];

  useEffect(() => {
    fetchStaff(1, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, sortBy, sortOrder]);

  return (
    <div className="page-content rp-page-content">
      <Container fluid>
        <div className="rp-page">
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16, flexWrap: "wrap", gap: "12px" }}>
            <div>
              <h2 className="rp-card__title" style={{ margin: 0 }}>Staff</h2>
              <p className="rp-card__subtitle" style={{ margin: 0 }}>Overview of restaurant staff members and their roles.</p>
            </div>
            <SecondaryButton
              variant="orange"
              icon={tickIcon}
              onClick={() => navigate("/restaurant/staff/create")}
            >
              Add Staff
            </SecondaryButton>
          </div>
          <FilterToolbar
            searchValue={search}
            onSearchChange={handleSearchChange}
            searchPlaceholder="Search by name or email"
            filters={filters}
            showExport
            onExport={handleExport}
          />

          <DataTable
            columns={columns}
            rows={staff}
            getRowKey={(r) => r.id}
            loading={loading}
            loadingMore={loadingMore}
            onLoadMore={handleLoadMore}
            hasMore={hasMore}
          />
        </div>
      </Container>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
          }}
        >
          <div
            style={{
              backgroundColor: "white",
              padding: "24px",
              borderRadius: "12px",
              maxWidth: "400px",
              width: "90%",
              boxShadow: "0 4px 20px rgba(0, 0, 0, 0.15)",
            }}
          >
            <h3
              style={{
                marginBottom: "12px",
                fontSize: "20px",
                fontWeight: 600,
                color: "#1a1a1a",
              }}
            >
              Delete Staff Member
            </h3>
            <p
              style={{
                marginBottom: "24px",
                fontSize: "14px",
                color: "#666",
                lineHeight: "1.5",
              }}
            >
              Are you sure you want to delete this staff member? This action cannot be undone.
            </p>
            <div
              style={{
                display: "flex",
                gap: "12px",
                justifyContent: "flex-end",
              }}
            >
              <button
                onClick={cancelDelete}
                style={{
                  padding: "10px 20px",
                  borderRadius: "8px",
                  border: "1px solid #ddd",
                  backgroundColor: "white",
                  color: "#666",
                  fontSize: "14px",
                  fontWeight: 500,
                  cursor: "pointer",
                }}
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                style={{
                  padding: "10px 20px",
                  borderRadius: "8px",
                  border: "none",
                  backgroundColor: "#dc3545",
                  color: "white",
                  fontSize: "14px",
                  fontWeight: 500,
                  cursor: "pointer",
                }}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

RestaurantStaffPage.propTypes = {
  onFilterClick: PropTypes.func,
  onExportClick: PropTypes.func,
};

RestaurantStaffPage.defaultProps = {
  onFilterClick: () => {},
  onExportClick: () => {},
};

export default RestaurantStaffPage;